/**
 * React dependencies
 */

import React, { useEffect, useRef } from 'react';

/**
 * DWT Dependencies
 */

import { closeButtonIcon, searchIcon } from '../.././img/icons';

/**
 * DWT components
 */

import NavOpenButton from './NavOpenButton';
import NavCloseButton from './NavCloseButton';

/**
 * Search bar component
 *
 * A component to render a search input field for the nav
 *
 * @param {Object}  props
 * @param {boolean} props.isDesktop
 * @param {boolean} props.isOpenSearch
 * @param {Object}  props.setIsOpenSearch
 * @param {string}  props.searchText
 * @param {Object}  props.setSearchText
 */
const NavSearchBar = ({ isDesktop, isOpenSearch, setIsOpenSearch, searchText, setSearchText }) => {
  const ref = useRef( null );

  const keyboardInputHandler = ( e ) => {
    const searchOpen = document.querySelectorAll( '.search-bar-active' )[0];
    if ( !searchOpen && isDesktop ) {
      return;
    }
    const focusableEls = document.querySelectorAll( '.search-bar-active .selectable' );
    const firstFocusable = focusableEls[0];
    const lastFocusable = focusableEls[ focusableEls.length - 1 ];
    let tabInSearch = false;
    if ( e.key === 'Escape' ) {
      closeSearch();
    }
    if ( e.key === 'Enter' ) {
      // If empty search or clicking close button
      if ( searchText === '' || document.activeElement === lastFocusable ) {
        closeSearch();
        return;
      }
      navigateToSearch();
    }
    if ( e.key === 'Tab' ) {
      for ( var i = 0; i < focusableEls.length; i++ ) {
        if ( document.activeElement === focusableEls[i] ){
          tabInSearch = true;
          break;
        }
      }
      if ( e.shiftKey ){
        if( document.activeElement === firstFocusable ){
          lastFocusable.focus();
          e.preventDefault();
          return;
        }
      } else {
        if ( document.activeElement === lastFocusable || !tabInSearch ){
          firstFocusable.focus();
          e.preventDefault();
          return;
        }
      }
    }
  };

  const navigateToSearch = () => {
    const targetUrl = `/search?keyword=${searchText}`;
    window.location.assign( targetUrl );
  };

  const closeSearch = () => {
    const searchOpen = document.querySelectorAll( '.search-bar-active' )[0];
    if ( !searchOpen ) {
      return;
    }
    setSearchText( '' );
    setIsOpenSearch( false );
    setTimeout( () => {
      const openButton = document.getElementsByClassName( 'search__btn--open' )[0];
      if ( openButton ) openButton.focus();
    }, 100 );
  };

  const handleSearchText = ( e ) => setSearchText( e.target.value );

  const handleOnClickSearchBtn = () => {
    if ( !isOpenSearch && searchText !== '' ) {
      setSearchText( '' );
    }
    const searchBar = document.getElementsByClassName( 'search-input' );
    searchBar[0].focus();

    setIsOpenSearch( !isOpenSearch );
  };

  const resetSearch = ( e ) => {
    if ( ref && ref.current && !ref.current.contains( e.target ) ) {
      closeSearch();
    }
  };

  const removeClassOnEnd = ( e ) => {
    if ( !ref.current.classList.contains( 'is-active' ) ) {
      e.target.parentNode.parentNode.classList.remove( 'is-hidden-menu-items' );
    }
  };

  const applyClassOnStart = ( e ) => {
    if ( ref.current.classList.contains( 'is-active' ) ) {
      e.target.parentNode.parentNode.classList.add( 'is-hidden-menu-items' );
    }
  };

  useEffect( () => {
    document.body.classList.toggle( 'is-open-search', isOpenSearch );
    ref.current.addEventListener( 'transitionstart', applyClassOnStart );
    document.addEventListener( 'mousedown', resetSearch );

    return () => {
      ref.current.removeEventListener( 'transitionstart', applyClassOnStart );
      document.removeEventListener( 'mousedown', resetSearch );
    };
  }, [ ref, isOpenSearch ] );

  return (
    <div className={ `flex flex-row items-end w-[100%] ${isOpenSearch ? 'search-bar-active' : ''}`} onKeyDown={ ( e ) => keyboardInputHandler( e ) }>
      <div className={`button-and-input flex ${isDesktop ? 'items-center' : 'w-[100%]'} ${isOpenSearch ? 'search-open' : ''}`}>
        { isOpenSearch && ( searchIcon ) }
        <input
          ref={ ref }
          id='site-search'
          className={ `selectable search-input ${isOpenSearch ? 'is-active' : ''}` }
          type='search'
          value={ searchText }
          onChange={ ( e ) => handleSearchText( e ) }
          onTransitionEnd={ ( e ) => removeClassOnEnd( e ) }
          aria-label='Search'
          tabIndex={ isOpenSearch ? 0 : -1 }
        />
        <NavOpenButton
          openButtonCondition={ !isOpenSearch }
          openButtonClassNames="mr-20 md:mr-50 xl:mr-0 search__btn--open"
          openIcon={ searchIcon }
          handleOpenClose={ isDesktop ? handleOnClickSearchBtn : navigateToSearch }
          ariaLabel='Open Search'
        />
      </div>
      <div>
        <NavCloseButton
          closeButtonCondition={ isOpenSearch }
          closeButtonClassNames='selectable search__btn--close'
          closeIcon={ closeButtonIcon }
          handleOpenClose={ isDesktop ? handleOnClickSearchBtn : null }
          ariaLabel='Close Search'
        />
      </div>
    </div>
  );
};

export default NavSearchBar;
